import {
  ChatManageRelationshipAction,
  ChatRelationshipEntityRef,
  ChatRelationshipKey,
  ChatRelationshipOperation,
} from "../../types/chat/ChatContent";

import { CONTEXT_ENTITY_TYPES, isRecord } from "./chatWireGuards";
import { namesRelatedRecord, RELATIONSHIP_ENTITY_TYPES } from "./manageRelationshipPayload";

// The platform's closed set. A fifth key would arrive as an unknown proposal rather than render a
// relationship this build cannot describe.
const RELATIONSHIP_KEYS: ChatRelationshipKey[] = [
  "account.contacts",
  "account.deals",
  "account.parentAccount",
  "account.primaryContact",
];

const RELATIONSHIP_OPERATIONS: ChatRelationshipOperation[] = ["link", "unlink"];

// Absent and null both mean "no record on this end", and the platform sends both: previousEntity is
// null when a scalar link was empty, and absent for a set. Either way the caller gets undefined, so
// nothing downstream has to test for two empties. A malformed ref is a different thing entirely and
// fails the whole action.
const parseRelationshipEntityRef = (
  value: unknown
): { ok: false } | { ok: true; ref?: ChatRelationshipEntityRef } => {
  if (value === undefined || value === null) {
    return { ok: true };
  }
  const entityType = RELATIONSHIP_ENTITY_TYPES.find(
    (candidate) => isRecord(value) && candidate === value.entityType
  );
  if (!isRecord(value) || !entityType || typeof value.name !== "string") {
    return { ok: false };
  }
  if (typeof value.id !== "number") {
    return { ok: false };
  }
  return { ok: true, ref: { id: value.id, entityType, name: value.name } };
};

const parseManageRelationshipAction = (
  value: Record<string, unknown>
): ChatManageRelationshipAction | undefined => {
  const entityType = CONTEXT_ENTITY_TYPES.find((candidate) => candidate === value.entityType);
  const relationshipKey = RELATIONSHIP_KEYS.find(
    (candidate) => candidate === value.relationshipKey
  );
  const operation = RELATIONSHIP_OPERATIONS.find((candidate) => candidate === value.operation);
  if (
    typeof value.displayName !== "string" ||
    typeof value.entityId !== "number" ||
    !entityType ||
    !relationshipKey ||
    !operation ||
    (value.setPrimary !== undefined && typeof value.setPrimary !== "boolean")
  ) {
    return undefined;
  }
  const relatedEntity = parseRelationshipEntityRef(value.relatedEntity);
  const previousEntity = parseRelationshipEntityRef(value.previousEntity);
  if (!relatedEntity.ok || !previousEntity.ok) {
    return undefined;
  }
  // A link with nothing to link is unrenderable — the card would have a title and no subject. A
  // collection unlink with nothing to unlink is the same defect: there the record IS the subject.
  // Both fail on namesRelatedRecord, which asks a wire question. Do NOT reach for
  // canChooseRelatedRecord here: it answers what the rep may edit, and narrowing it once already
  // turned this guard off for every unlink without a single test failing.
  if (!relatedEntity.ref && namesRelatedRecord({ operation, relationshipKey })) {
    return undefined;
  }
  return {
    displayName: value.displayName,
    entityId: value.entityId,
    entityType,
    operation,
    ...(previousEntity.ref ? { previousEntity: previousEntity.ref } : {}),
    ...(relatedEntity.ref ? { relatedEntity: relatedEntity.ref } : {}),
    relationshipKey,
    ...(value.setPrimary === undefined ? {} : { setPrimary: value.setPrimary }),
    type: "manage_relationship",
  };
};

export default parseManageRelationshipAction;
